import { Controller } from "@hotwired/stimulus";

import * as helpers from "../helpers"
import { getClientLabel } from "../helpers/get_client_label"

const clientSupportsWebAuthn = !!(window.PublicKeyCredential && navigator.credentials);

export default class extends Controller {
  static targets = [ "displayName", "errorOutput" ]

  static values = { 
    redirectAfterRegistration: {
      type: Boolean,
      default: false,
    },
    redirectAfterAuthentication: {
      type: Boolean,
      default: true,
    } 
  }

  connect() {
    if (this.hasErrorOutputTarget) {
      this.#hideError()
    }

    if (this.hasDisplayNameTarget) {
      this.displayNameTarget.value = getClientLabel()
    }
  }

  // needs displayName and errorOutput target setf
  async register(event) {
    event.preventDefault()

    if (!clientSupportsWebAuthn) {
      this.#showError("Your browser doesn't support Passkeys. Try updating your browser to its latest version.")
      return
    }

    let displayName = this.displayNameTarget.value.trim()
    if (displayName == "") {
      this.#showError("A display name is required for you to register credential.")
      return
    }

    const optionsResponse = await fetch("/home/webauthn/registrations/initiate", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    })

    if (optionsResponse.status < 200 || optionsResponse.status > 299) {
      const json = await optionsResponse.json()
      this.#showError(json.error)
      return
    }

    const options = await optionsResponse.json()

    let credential
    try {
      const publicKey = PublicKeyCredential.parseCreationOptionsFromJSON(options)
      credential = await navigator.credentials.create({ publicKey })
    } catch (error) {
      switch (error.name) {
        case "NotAllowedError": 
          this.#showError("Failed to register. Your browser might not be compatible, try updating its browser to the latest version.")
          break
        default:
          this.#showError(error)
      }
      return
    }
    
    const verifyResponse = await fetch("/home/webauthn/registrations/verify", { 
      method: "POST", 
      body: JSON.stringify({ 
        public_key_credential: credential, 
        display_name: this.displayNameTarget.value,
        redirect_after: this.redirectAfterRegistrationValue
      }),
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": helpers.getMetaValue("csrf-token"),
      }, 
    })

    if (verifyResponse.status < 200 || verifyResponse.status > 299) {
      const json = await verifyResponse.json()
      this.#showError(json.error)
      return
    }

    // success
    this.#hideError()

    if (verifyResponse.redirected) {
      window.location.replace(verifyResponse.url)
    }
  }

  // needs errorOutput target set
  async authenticate(event) {
    event.preventDefault()

    if (!clientSupportsWebAuthn) {
      this.#showError("Your browser doesn't support Passkeys. Try updating your browser to its latest version.")
      return
    }

    const optionsResponse = await fetch("/home/webauthn/authentication/initiate", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    })

    if (optionsResponse.status < 200 || optionsResponse.status > 299) {
      const json = await optionsResponse.json()
      this.#showError(json.error)
      return
    }

    const options = await optionsResponse.json()

    let credential
    try {
      const publicKey = PublicKeyCredential.parseRequestOptionsFromJSON(options)
      credential = await navigator.credentials.get({ publicKey })
    } catch (error) {
      switch (error.name) {
        case "NotAllowedError": 
          this.#showError("Failed to authenticate. Sign in with email and password instead.")
          break
        default:
          this.#showError(error)
      }
      return
    }

    const verifyResponse = await fetch("/home/webauthn/authentication/verify", { 
        method: "POST", 
        body: JSON.stringify({ 
          public_key_credential: credential,
          redirect_after: this.redirectAfterAuthenticationValue,
        }),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": helpers.getMetaValue("csrf-token"),
        }, 
    })

    if (verifyResponse.status < 200 || verifyResponse.status > 299) {
      const json = await verifyResponse.json()
      this.#showError(json.error)
      return
    }

    // success
    this.#hideError()

    if (verifyResponse.redirected) {
      window.location.replace(verifyResponse.url)
    }
  }

  #showError(errorMessage) {
    this.errorOutputTarget.innerText = "Error: " + errorMessage
    this.errorOutputTarget.classList.remove("hidden")
    this.errorOutputTarget.setAttribute('aria-hidden', 'false')
  }

  #hideError() {
    this.errorOutputTarget.innerText = ""
    this.errorOutputTarget.classList.add("hidden")
    this.errorOutputTarget.setAttribute('aria-hidden', 'true')
  }
}
